const DATABASE_NAME = 'scatr-demo-local';
const DATABASE_VERSION = 1;
const STORE_NAME = 'scans';

const openDatabase = () => new Promise((resolve, reject) => {
    if (!window.indexedDB) {
        reject(new Error('IndexedDB is not available in this browser.'));
        return;
    }
    const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
            request.result.createObjectStore(STORE_NAME, { keyPath: 'map_id' });
        }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
});

const readAll = async () => {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
        const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

const put = async scan => {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        transaction.objectStore(STORE_NAME).put(scan);
        transaction.oncomplete = () => resolve(scan);
        transaction.onerror = () => reject(transaction.error);
    });
};

const get = async mapID => {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
        const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(mapID);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
    });
};

const clone = value => (value == null ? value : JSON.parse(JSON.stringify(value)));

export const createLocalScan = async record => {
    const map_id = window.crypto?.randomUUID ? window.crypto.randomUUID() : `local-${Date.now()}-${Math.random()}`;
    const created_at = new Date().toISOString();
    const serial = Date.now();
    return put({
        ...record,
        map_id,
        serial,
        key: serial,
        created_at,
        date: new Date(created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        results: [],
        spectra: [],
        darkReferenceSpectrum: null,
        image: null,
        test_strips: {},
        archived: false,
        local_only: true
    });
};

export const getLocalScans = async ({ username, dateRange, archived = false }) => {
    const from = dateRange?.from ? new Date(dateRange.from).getTime() : -Infinity;
    const to = dateRange?.to ? new Date(dateRange.to).getTime() : Infinity;
    const scans = await readAll();
    return scans
        .filter(scan => scan.username === username && scan.archived === archived)
        .filter(scan => {
            const created = new Date(scan.created_at).getTime();
            return created >= from && created <= to;
        })
        .sort((a, b) => b.serial - a.serial);
};

export const updateLocalScan = async record => {
    const current = await get(record.map_id);
    if (!current) throw new Error(`Local scan ${record.map_id} was not found.`);
    const metadataFields = [
        'client_code', 'expected_substances', 'substance_strength',
        'substance_description', 'impact', 'notes', 'operator_id',
        'image_filename', 'testing_for', 'consumed', 'lab_sample_id',
        'gender', 'age', 'chronic_pain', 'indigenous', 'brpoc', 'lgbtq',
        'preferred_language'
    ];
    const updated = { ...current };
    metadataFields.forEach(field => {
        if (Object.prototype.hasOwnProperty.call(record, field)) {
            updated[field] = clone(record[field]);
        }
    });
    return put(updated);
};

export const getLocalScan = get;

export const saveLocalSpectra = async (mapID, body) => {
    const scan = await get(mapID);
    if (!scan) throw new Error(`Local scan ${mapID} was not found.`);
    const spectra = body.indices.map((index, i) => ({
        index,
        coordinate: body.coordinates[i],
        laser_power: body.laserPowers[i],
        integration_time: body.integrationTimes[i],
        raw_spectrum: body.rawSpectra[i],
        processed_spectrum: body.processedSpectra[i],
        label: body.labels[i]
    }));
    return put({ ...scan, spectra });
};

export const getLocalSpectra = async mapID => (await get(mapID))?.spectra || [];

export const saveLocalResults = async (mapID, results) => {
    const scan = await get(mapID);
    if (!scan) throw new Error(`Local scan ${mapID} was not found.`);
    return put({ ...scan, results: clone(results) || [] });
};

export const getLocalResults = async mapID => (await get(mapID))?.results || [];

export const saveLocalDarkReference = async (mapID, darkReferenceSpectrum) => {
    const scan = await get(mapID);
    if (!scan) throw new Error(`Local scan ${mapID} was not found.`);
    return put({ ...scan, darkReferenceSpectrum: clone(darkReferenceSpectrum) });
};

export const getLocalDarkReference = async mapID => (await get(mapID))?.darkReferenceSpectrum || null;

export const saveLocalImage = async (mapID, file) => {
    const scan = await get(mapID);
    if (!scan) throw new Error(`Local scan ${mapID} was not found.`);
    return put({ ...scan, image: { filename: file.name, type: file.type, blob: file } });
};

export const getLocalImage = async mapID => (await get(mapID))?.image || null;

export const getLocalImageBySerial = async serial => {
    const scan = (await readAll()).find(item => String(item.serial) === String(serial));
    return scan?.image || null;
};

export const saveLocalTestStrips = async (mapID, testStrips) => {
    const scan = await get(mapID);
    if (!scan) throw new Error(`Local scan ${mapID} was not found.`);
    return put({ ...scan, test_strips: clone(testStrips) || {} });
};

export const getLocalTestStrips = async mapID => {
    const scan = await get(mapID);
    return Object.entries(scan?.test_strips || {}).map(([target_substance, result]) => ({ target_substance, result }));
};

export const archiveLocalScan = async mapID => {
    const scan = await get(mapID);
    if (!scan) return { success: false, message: 'Scan not found.' };
    await put({ ...scan, archived: true });
    return { success: true, message: 'Scan archived.' };
};

export const restoreLocalScan = async mapID => {
    const scan = await get(mapID);
    if (!scan) return { success: false, message: 'Scan not found.' };
    await put({ ...scan, archived: false });
    return { success: true, message: 'Scan restored.' };
};
